import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  DarkMode,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { CalendarClock } from 'lucide-react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  GATHERING_LIMITS,
  gatheringEnded,
  WS_EVENTS,
  type WsGatheringPayload,
  type WsGatheringRingPayload,
} from '@twinden/shared';
import { useWebSocket } from '../../hooks/useWebSocket';
import { api } from '../../lib/api';
import { gatheringErrorMessage } from '../../lib/gatherings';
import { gatheringStartOf, gatheringTitleOf } from '../../lib/gatheringTitles';
import { startGatheringTune, stopVibration } from '../../lib/sounds';
import {
  alertSettingsAtom,
  currentUserAtom,
  gatheringRingAtom,
  messagesAtom,
} from '../../store';
import { useT } from '../../i18n';

// "Come now" (D286): a full-screen ring for someone not yet at the table. It
// is not a call (D278): its own tune, two plain choices, no media. The words
// were opened on this device, so the title comes from the chat's messages in
// memory or from any gathering opened this visit (history too); when it was
// never opened here the ring says only "Come now".

/** Has this user arrived, or is the gathering over? Then no ring (Review Focus 5). */
function shouldClose(
  gathering: WsGatheringPayload['gathering'] | undefined,
  userId: string | undefined
): boolean {
  if (!gathering) return false;
  // Over by the clock too: a ring held through a long call mustn't come
  // back after the gathering's window (D288).
  if (
    gathering.cancelledAt ||
    gathering.ended ||
    gatheringEnded(gathering, Date.now())
  )
    return true;
  return (gathering.replies ?? []).some(
    (reply) => reply.userId === userId && Boolean(reply.arrivedAt)
  );
}

export function GatheringRing({
  held = false,
}: {
  /**
   * A call is live or ringing: the ring waits, silent and hidden, and shows
   * when the call ends if it still applies (the call is louder, D278).
   */
  held?: boolean;
} = {}) {
  const t = useT();
  const toast = useToast();
  const { onEvent } = useWebSocket();
  const ring = useAtomValue(gatheringRingAtom);
  const setRing = useSetAtom(gatheringRingAtom);
  const messages = useAtomValue(messagesAtom);
  const user = useAtomValue(currentUserAtom);
  const settings = useAtomValue(alertSettingsAtom);
  const [busy, setBusy] = useState(false);
  // The ring that came back after "5 more minutes": no second snooze.
  const [repeated, setRepeated] = useState<string | null>(null);
  const snooze = useRef<{
    timer: number;
    payload: WsGatheringRingPayload;
  } | null>(null);
  const userId = user?.id;

  const cancelSnooze = useCallback(() => {
    if (snooze.current) window.clearTimeout(snooze.current.timer);
    snooze.current = null;
  }, []);
  useEffect(() => cancelSnooze, [cancelSnooze]);

  const message = ring
    ? (messages[ring.conversationId] ?? []).find((m) => m.id === ring.messageId)
    : undefined;
  const state = (message as { gathering?: WsGatheringPayload['gathering'] })
    ?.gathering;
  const title = (
    (message as { e2ee?: { envelope?: { gathering?: { title?: string } } } })
      ?.e2ee?.envelope?.gathering?.title ??
    (ring ? gatheringTitleOf(ring.messageId) : undefined)
  )?.trim();

  // A newer ring for the same gathering replaces a snoozed one: the snooze
  // must not bring the older one back over it.
  useEffect(() => {
    if (ring && snooze.current?.payload.messageId === ring.messageId) {
      cancelSnooze();
    }
  }, [ring, cancelSnooze]);

  // Arrived or over elsewhere: this device stops ringing without a tap.
  useEffect(() => {
    return onEvent(({ event, payload }) => {
      if (event !== WS_EVENTS.MESSAGE_GATHERING) return;
      const update = payload as WsGatheringPayload;
      if (!shouldClose(update.gathering, userId)) return;
      if (snooze.current?.payload.messageId === update.messageId) {
        cancelSnooze();
      }
      setRing((current) =>
        current?.messageId === update.messageId ? null : current
      );
    });
  }, [onEvent, userId, setRing, cancelSnooze]);

  // Over by the clock, when the gathering was opened from history and isn't
  // in memory (D288): checked again whenever this re-renders, e.g. as a
  // call that held the ring ends.
  const startsAt =
    state?.startsAt ?? (ring ? gatheringStartOf(ring.messageId) : undefined);
  const overByClock = Boolean(
    startsAt &&
    Date.now() >= Date.parse(startsAt) + GATHERING_LIMITS.OPEN_AFTER_START_MS
  );

  // The chat is in memory and says so already.
  useEffect(() => {
    if (ring && (overByClock || shouldClose(state, userId))) setRing(null);
  }, [ring, state, userId, setRing, overByClock, held]);

  // The tune plays while the overlay is up and stops with it (D278).
  const showing = Boolean(ring) && !held && !overByClock;
  useEffect(() => {
    if (!showing) return;
    const stop = startGatheringTune({
      sound: settings.callRingtone,
      vibrate: settings.vibration,
    });
    return () => {
      stop();
      stopVibration();
    };
  }, [showing, ring, settings.callRingtone, settings.vibration]);

  if (!ring || held || overByClock) return null;

  const key = `${ring.messageId}:${ring.ring}`;
  const heading = !title
    ? t('chat.gathering.ringGeneric')
    : title.toLowerCase() === 'dinner' ||
        title.toLowerCase() === t('chat.gathering.quickDinner').toLowerCase()
      ? t('chat.gathering.ringDinner')
      : t('chat.gathering.ringTitle', { title });

  const coming = async () => {
    setBusy(true);
    try {
      await api.arriveAtGathering(ring.messageId);
      cancelSnooze();
      setRing(null);
    } catch (err) {
      toast({
        status: 'error',
        title: gatheringErrorMessage(err),
        duration: 4000,
      });
    } finally {
      setBusy(false);
    }
  };

  // This device only: nothing is sent, and it shows once more (D287).
  const later = () => {
    const payload = ring;
    cancelSnooze();
    setRing(null);
    snooze.current = {
      payload,
      timer: window.setTimeout(() => {
        snooze.current = null;
        setRepeated(`${payload.messageId}:${payload.ring}`);
        // Another ring that came meanwhile stays on screen.
        setRing((current) => current ?? payload);
      }, GATHERING_LIMITS.SNOOZE_MS),
    };
  };

  return (
    <DarkMode>
      <Box
        data-theme="dark"
        data-testid="gathering-ring"
        role="alertdialog"
        aria-modal="true"
        aria-label={heading}
        position="fixed"
        inset={0}
        h="var(--app-height, 100vh)"
        zIndex={2050}
        bg="#1B1712"
        color="#F3F0EB"
        pt="env(safe-area-inset-top)"
        pb="env(safe-area-inset-bottom)"
        px={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={8} maxW="360px" w="100%" textAlign="center">
          <Box color="#F0B63F">
            <CalendarClock size={64} aria-hidden="true" />
          </Box>
          <Text as="h1" fontSize="28px" fontWeight={700} lineHeight="1.25">
            {heading}
          </Text>
          <VStack spacing={3} w="100%">
            <Button
              w="100%"
              size="lg"
              colorScheme="brand"
              isLoading={busy}
              onClick={() => void coming()}
            >
              {t('chat.gathering.ringComing')}
            </Button>
            {repeated !== key && (
              <Button
                w="100%"
                size="lg"
                variant="outline"
                borderColor="whiteAlpha.400"
                isDisabled={busy}
                onClick={later}
              >
                {t('chat.gathering.ringSnooze')}
              </Button>
            )}
          </VStack>
        </VStack>
      </Box>
    </DarkMode>
  );
}
